import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { formatDate } from '@angular/common';
import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    ColDef,
    GridApi,
    RenderApiModule,
    ClientSideRowModelApiModule,
    ICellRendererParams,
} from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CurrencyApiService } from 'src/app/core/api/currency-api.service';
import { CurrencyOverviewRow } from 'src/app/core/models/contract-currency';
import { SpinnerService } from 'src/app/core/services/spinner.service';

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
]);

@Component({
    selector: 'app-currency-overview',
    standalone: true,
    imports: [AgGridAngular, FormsModule, TranslatePipe],
    templateUrl: './currency-overview.component.html',
    styleUrl: './currency-overview.component.scss',
})
export class CurrencyOverviewComponent implements OnInit {
    public allRows: CurrencyOverviewRow[] = [];
    public rowData: CurrencyOverviewRow[] = [];
    public colDefs: ColDef<CurrencyOverviewRow>[] = [];
    public selectedCurrency: string = 'all';
    public currencyOptions: string[] = ['all', 'EUR', 'USD', 'GBP'];
    // EndDate month filter (YYYY-MM from a native month picker) — same control and logic as contracts-overview.
    public selectedMonth: string = '';
    private gridApi: GridApi<CurrencyOverviewRow>;

    public defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
    };

    // Re-evaluated reliably by AG Grid on every refreshCells, unlike getRowStyle which only runs on row creation.
    public rowClassRules = {
        'inserted-in-bc-row': (params: any) => !!params.data?.insertedInBc,
    };

    constructor(
        private currencyApiService: CurrencyApiService,
        private translateService: TranslateService,
        private spinner: SpinnerService,
    ) {}

    ngOnInit(): void {
        this.spinner.show();
        this.currencyApiService.getCurrencyOverview().subscribe({
            next: (rows) => {
                this.allRows = rows;
                this.applyFilter();
                this.buildColDefs();
                this.spinner.hide();
            },
            error: () => this.spinner.hide(),
        });
    }

    public onGridReady(params: any): void {
        this.gridApi = params.api;
        this.gridApi.setGridOption('rowData', this.rowData);
    }

    public onCurrencyFilterChange(): void {
        this.applyFilter();
        this.updateColumnVisibility();
    }

    public onMonthFilterChange(): void {
        this.applyFilter();
    }

    public clearMonthFilter(): void {
        this.selectedMonth = '';
        this.applyFilter();
    }

    private applyFilter(): void {
        let rows = this.selectedCurrency === 'all'
            ? [...this.allRows]
            : this.allRows.filter(r => r.currency === this.selectedCurrency);

        // EndDate month filter — mirrors contracts-overview applyFilters() exactly.
        if (this.selectedMonth) {
            const [year, month] = this.selectedMonth.split('-').map(Number);
            rows = rows.filter(r => {
                if (!r.endDate) return false;
                const d = new Date(r.endDate);
                return d.getFullYear() === year && d.getMonth() + 1 === month;
            });
        }

        this.rowData = rows;
        this.gridApi?.setGridOption('rowData', this.rowData);
    }

    private updateColumnVisibility(): void {
        if (!this.gridApi) return;
        const currencies = ['EUR', 'USD', 'GBP'];
        if (this.selectedCurrency === 'all') {
            this.gridApi.setColumnsVisible(
                currencies.flatMap(c => [`price_${c}`, `rate_${c}`]),
                true
            );
        } else {
            currencies.forEach(c => {
                const visible = c === this.selectedCurrency;
                this.gridApi.setColumnsVisible([`price_${c}`, `rate_${c}`], visible);
            });
        }
    }

    private formatGridDate = (params: any): string =>
        params.value ? formatDate(params.value, 'dd-MM-yyyy', 'en') : '';

    private dateFilterGetter = (field: 'endDate') => (params: any): string =>
        params.data?.[field] ? formatDate(params.data[field], 'dd-MM-yyyy', 'en') : '';

    /**
     * Toggles the InsertedInBC flag for a row. Called from the checkbox's native change
     * handler (wired in the cell renderer). On success it mutates the row node's data —
     * the same field rowClassRules reads — and forces a cell refresh so the grey/disabled
     * state re-applies instantly without a reload.
     */
    private toggleInsertedInBc(node: any, newValue: boolean): void {
        const row: CurrencyOverviewRow = node.data;
        if (!row) return;
        this.currencyApiService.setInsertedInBc(row.contractCurrencyId, newValue).subscribe({
            next: () => {
                node.setDataValue('insertedInBc', newValue);
                this.gridApi?.refreshCells({ force: true, rowNodes: [node] });
                this.gridApi?.redrawRows({ rowNodes: [node] });
            },
            error: () => {
                // Revert the checkbox to its previous state on failure.
                this.gridApi?.refreshCells({ force: true, rowNodes: [node] });
            },
        });
    }

    private buildColDefs(): void {
        const t = this.translateService;
        this.colDefs = [
            {
                field: 'customerName',
                headerName: t.instant('currencyOverview.customerName'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 130,
            },
            {
                field: 'consultantName',
                headerName: t.instant('currencyOverview.consultantName'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 130,
            },
            // Sparse Timepris columns — show value only when row currency matches
            {
                colId: 'price_EUR',
                headerName: t.instant('currencyOverview.priceEur'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 120,
                valueGetter: (params: any) =>
                    params.data?.currency === 'EUR' ? params.data.price : '',
            },
            {
                colId: 'price_USD',
                headerName: t.instant('currencyOverview.priceUsd'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 120,
                valueGetter: (params: any) =>
                    params.data?.currency === 'USD' ? params.data.price : '',
            },
            {
                colId: 'price_GBP',
                headerName: t.instant('currencyOverview.priceGbp'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 120,
                valueGetter: (params: any) =>
                    params.data?.currency === 'GBP' ? params.data.price : '',
            },
            // Sparse kurs columns
            {
                colId: 'rate_EUR',
                headerName: t.instant('currencyOverview.rateEur'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 140,
                valueGetter: (params: any) => {
                    const row: CurrencyOverviewRow = params.data;
                    if (!row || row.currency !== 'EUR') return '';
                    return row.rateMonthYear
                        ? `${row.exchangeRate} (${row.rateMonthYear})`
                        : `${row.exchangeRate}`;
                },
            },
            {
                colId: 'rate_USD',
                headerName: t.instant('currencyOverview.rateUsd'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 140,
                valueGetter: (params: any) => {
                    const row: CurrencyOverviewRow = params.data;
                    if (!row || row.currency !== 'USD') return '';
                    return row.rateMonthYear
                        ? `${row.exchangeRate} (${row.rateMonthYear})`
                        : `${row.exchangeRate}`;
                },
            },
            {
                colId: 'rate_GBP',
                headerName: t.instant('currencyOverview.rateGbp'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 140,
                valueGetter: (params: any) => {
                    const row: CurrencyOverviewRow = params.data;
                    if (!row || row.currency !== 'GBP') return '';
                    return row.rateMonthYear
                        ? `${row.exchangeRate} (${row.rateMonthYear})`
                        : `${row.exchangeRate}`;
                },
            },
            // Timepris kurs måned år — always show for the row's own currency
            {
                colId: 'rateMonthYear',
                headerName: t.instant('currencyOverview.priceRateMonthYear'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 150,
                valueGetter: (params: any) => {
                    const row: CurrencyOverviewRow = params.data;
                    if (!row) return '';
                    return row.rateMonthYear
                        ? `${row.exchangeRate} (${row.rateMonthYear})`
                        : `${row.exchangeRate}`;
                },
            },
            // EndDate with text-date filter pattern
            {
                field: 'endDate',
                headerName: t.instant('currencyOverview.endDate'),
                valueFormatter: this.formatGridDate,
                filterValueGetter: this.dateFilterGetter('endDate'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 110,
            },
            // Sat ind i BC — checkbox with a real change listener so the toggle reliably
            // fires (a native <input> swallows the click that onCellClicked relies on).
            {
                field: 'insertedInBc',
                headerName: t.instant('currencyOverview.insertedInBc'),
                width: 130,
                filter: false,
                sortable: false,
                cellRenderer: (params: ICellRendererParams<CurrencyOverviewRow>) => {
                    const input = document.createElement('input');
                    input.type = 'checkbox';
                    input.style.cursor = 'pointer';
                    input.checked = !!params.data?.insertedInBc;
                    input.addEventListener('change', () => {
                        this.toggleInsertedInBc(params.node, input.checked);
                    });
                    return input;
                },
            },
        ];
    }
}
